'use client'

import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { type Chain, custom, http, RpcRequestError, type Transport } from 'viem'
import { getHttpRpcClient } from 'viem/utils'
import {
  type Config,
  cookieStorage,
  createConfig,
  type CreateConfigParameters,
  type CreateConnectorFn,
  createStorage,
  fallback
} from 'wagmi'
import { baseAccount, mock } from 'wagmi/connectors'

import type { ChainId } from '../web3.types'
import type { Chain as AppChain } from './chains/chain.config'

import { getChain, getChains } from '../utils/chain.utils'
import { getAnvilRpcUrl } from './anvil'
import { limitBatchCalldataBytes } from './batching'
import { rpcs } from './rpcs'

type WagmiRpcOptions = {
  include?: Partial<{
    secret: boolean
    proxied: boolean
    public: boolean
  }>
  /**
   * Reads `ANVIL_RPC_URLS` / `ERPC_SECRET`. Only consulted when `include.secret`
   * is set; optional otherwise, so consumers that bring their own `transports`
   * (and never resolve secret RPC URLs) don't have to pass a no-op.
   */
  getEnvVar?: (name: 'ANVIL_RPC_URLS' | 'ERPC_SECRET') => string | undefined
  /**
   * Use only the caller's explicitly provided connectors and transports. The same caller-owned
   * Wagmi config is installed on the AppKit adapter and returned for `WagmiProvider`; browser
   * discovery and AppKit-added fallbacks are off.
   */
  explicitOnly?: boolean
}

function getRpcUrlsForChain(chain: Chain, opts: WagmiRpcOptions): string[] {
  const include = opts.include ?? {}
  const chainId = chain.id as ChainId
  const urls: (string | undefined)[] = []

  if (include.secret) {
    if (!opts.getEnvVar) {
      throw new Error('getWagmiAdapter: `getEnvVar` is required when `include.secret` is set')
    }
    urls.push(getAnvilRpcUrl(chainId, opts.getEnvVar))
    urls.push(
      rpcs.erpc.chain(chainId).with({ secret: opts.getEnvVar('ERPC_SECRET'), endpoint: 'cache' })
        .url
    )
  }
  if (include.proxied) urls.push(`/api/rpc/${chainId}`)
  if (include.public) urls.push(...chain.rpcUrls.default.http)

  const filtered = urls.filter((u): u is string => !!u)
  if (filtered.length === 0) {
    console.warn(`No RPC URLs found for ${chain.name}. Check \`include\` args and env vars.`)
  }
  return filtered
}

const defaultConnectors = [
  // Pre-configured to disable Base Account's telemetry. AppKit auto-injects
  // baseAccount() (telemetry on by default) unless a connector with id
  // 'baseAccount' already exists — so we register ours first.
  baseAccount({ preference: { options: 'all', telemetry: false } })
  // Intentionally NOT registering coinbaseWallet(): its 'com.coinbase.wallet'
  // rdns makes wagmi dedupe (hide) the Coinbase Prime extension, which announces
  // the same rdns via EIP-6963. See CRTR-2603.
]

/**
 * RPC deadline for every E2E request, wallet or read. Anvil answers a deploy
 * transaction only after pulling every cold storage slot it touches from the
 * fork upstream, which routinely runs past viem's 10s HTTP default; a real
 * wallet imposes no such deadline, so E2E waits comfortably longer.
 */
const E2E_RPC_TIMEOUT = 120_000

/**
 * wagmi's `mock` connector forwards `eth_sendTransaction` through viem's default
 * 10s HTTP timeout with `retryCount: 0`, so one slow anvil response aborts the
 * transaction outright. This re-issues that single method on a client with
 * {@link E2E_RPC_TIMEOUT} and delegates everything else untouched.
 *
 * It also tracks connection authorization per config. wagmi's `mock` reports
 * unauthorized without `features.reconnect`, so a `reconnect()` racing the E2E connect
 * click would otherwise wipe the live connection. `features.reconnect` alone would not
 * do: mock keeps that state in its own factory closure, shared by every config built
 * from this connector, and E2E builds two (the AppKit adapter's and the plain
 * `createConfig` below). Only `disconnect()`/`onDisconnect()` clear the flag, so a
 * teardown that writes wagmi state directly leaves the connector authorized and a later
 * `reconnect()` restores the session — the behavior E2E wants — while a fresh page load
 * starts unauthorized.
 *
 * E2E also skips wagmi's mount-time reconnect for the app config (see `Web3Provider`), so this
 * flag covers the AppKit adapter's own config and any later `reconnect()` caller.
 */
function e2eMock(parameters: Parameters<typeof mock>[0]): CreateConnectorFn {
  const create = mock(parameters)
  return config => {
    const connector = create(config)
    let hasConnection = false
    return {
      ...connector,
      async connect<withCapabilities extends boolean = false>(parameters?: {
        chainId?: number | undefined
        isReconnecting?: boolean | undefined
        withCapabilities?: withCapabilities | boolean | undefined
      }) {
        // Unbound on purpose: binding `this` needs explicit `.call` type arguments to keep the
        // `withCapabilities` relation, and mock's `connect` only issues locally-answered methods.
        const data = await connector.connect<withCapabilities>(parameters)
        hasConnection = true
        return data
      },
      async disconnect() {
        hasConnection = false
        await connector.disconnect.call(this)
      },
      async onDisconnect(error) {
        hasConnection = false
        await connector.onDisconnect?.call(this, error)
      },
      async isAuthorized() {
        return hasConnection
      },
      async getProvider(params?: { chainId?: number }) {
        const provider = await connector.getProvider(params)
        const chain = config.chains.find(c => c.id === params?.chainId) ?? config.chains[0]
        const [url] = chain.rpcUrls.default.http
        if (url === undefined) throw new Error(`No default RPC URL for chain ${chain.id}`)
        const client = getHttpRpcClient(url, { timeout: E2E_RPC_TIMEOUT })

        return custom({
          async request({ method, params: args }) {
            if (method !== 'eth_sendTransaction') return provider.request({ method, params: args })

            const body = { method, params: args }
            const { error, result } = await client.request({ body })
            if (error) throw new RpcRequestError({ body, error, url })
            return result
          }
        })({ retryCount: 0 })
      }
    }
  }
}

function e2eConnectors() {
  return [
    e2eMock({
      accounts: ['0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266'],
      features: { defaultConnected: true }
    })
  ]
}

function getRpcUrlsForChains(chainIds: readonly number[], opts: WagmiRpcOptions) {
  return Object.fromEntries(
    chainIds.map(chainId => {
      return [chainId, getRpcUrlsForChain(getChain(chainId), opts)]
    })
  ) as Record<(typeof chainIds)[number], string[]>
}

type GetWagmiAdapterReturnType = {
  wagmiConfig: Config
  wagmiAdapter: WagmiAdapter
}

/**
 * WalletConnect rejects a request with "Request expired" after 15 minutes by default. Custodial
 * wallets such as Fireblocks and hardware wallets can take longer to approve a transaction or a
 * permit signature, so these requests wait up to 2 hours for the wallet's answer instead. A longer
 * window would keep a cancelled transaction request approvable in the wallet while the user
 * resubmits, risking a duplicate execution. A late typed-data signature executes nothing by itself:
 * every signer here feeds a call the user still confirms. Keep it that way, or exclude the method.
 */
const WALLET_CONNECT_EXTENDED_REQUEST_EXPIRY_S = 2 * 60 * 60
const WALLET_CONNECT_EXTENDED_EXPIRY_METHODS = new Set([
  'eth_sendTransaction',
  'wallet_sendCalls',
  'eth_signTypedData_v4'
])

class MorphoWagmiAdapter extends WagmiAdapter {
  override async setUniversalProvider(
    universalProvider: Parameters<WagmiAdapter['setUniversalProvider']>[0]
  ) {
    const request = universalProvider.request.bind(universalProvider)
    universalProvider.request = (args, chain, expiry) =>
      request(
        args,
        chain,
        expiry ??
          (WALLET_CONNECT_EXTENDED_EXPIRY_METHODS.has(args.method)
            ? WALLET_CONNECT_EXTENDED_REQUEST_EXPIRY_S
            : undefined)
      )
    return super.setUniversalProvider(universalProvider)
  }
}

export function getWagmiAdapter({
  chainIds,
  projectId,
  transports,
  chains,
  connectors,
  batch,
  options
}: {
  chainIds: readonly ChainId[]
  projectId: string
  /**
   * Prebuilt transports per chain, keyed by chain id. Accepts any wagmi
   * `Transport` (a plain `http`, a `fallback([...])`, an instrumented proxy
   * transport, …) — a consumer that already builds its own transports doesn't
   * have to wrap each one in `fallback()` just to satisfy the type. When
   * omitted, the RPC-url map builds a `fallback()` transport per chain.
   */
  transports?: Record<number, Transport>
  chains?: [AppChain, ...AppChain[]]
  connectors?: CreateConnectorFn[]
  batch?: CreateConfigParameters['batch']
  options: WagmiRpcOptions & {
    ssr?: boolean
    isE2E?: boolean
    useCookieStorage?: boolean
    /**
     * Explicit wagmi storage. Takes precedence over `useCookieStorage` — e.g. a
     * consumer supplies `localStorage` for Safe-app iframes, where third-party
     * cookies are blocked. When omitted, storage falls back to
     * `useCookieStorage` (cookie storage by default).
     */
    storage?: CreateConfigParameters['storage']
  }
}): GetWagmiAdapterReturnType {
  // Set default options where necessary
  options.ssr ??= true
  options.useCookieStorage ??= true
  // Set default options.include where necessary (tailored for client)
  const include = { ...options?.include }
  include.secret ??= false
  include.proxied ??= true
  include.public ??= !options.isE2E
  // Constrain multicall.batchSize in E2E mode (to avoid overwhelming anvil)
  if (options.isE2E) {
    batch = limitBatchCalldataBytes(batch, 2 ** 15)
  }

  const rpcUrlsMap = getRpcUrlsForChains(chainIds, { ...options, include })

  let _chains = chains ?? getChains(chainIds)
  // NOTE: Mock connector uses default http URL to call `eth_sendTransaction`, so
  // we must redirect it to anvil in E2E mode
  if (options.isE2E) {
    _chains = _chains.map(chain => ({
      ...chain,
      rpcUrls: { default: { http: rpcUrlsMap[chain.id as number] } }
    })) as unknown as typeof _chains
  }

  // If no transports are passed in, create a fallback transport for all
  // RPC URLs with default retry counts/delays.
  // TODO: many of these `http` transports could take advantage of RPC-level
  // batching (independent of multicall)
  // Reads share the wallet's problem: anvil can sit on a request for far longer
  // than viem's 10s default, and an abort here just burns a retry cycle.
  const timeout = options.isE2E ? E2E_RPC_TIMEOUT : undefined
  const _transports =
    transports ??
    Object.fromEntries(
      Object.entries(rpcUrlsMap).map(([chainId, rpcUrls]) => {
        return [chainId, fallback(rpcUrls.map(rpcUrl => http(rpcUrl, { timeout })))]
      })
    )

  if (options.explicitOnly) {
    if (!connectors?.length) {
      throw new Error('getWagmiAdapter: explicitOnly mode requires non-empty explicit `connectors`')
    }

    const missingTransportChainIds = chainIds.filter(chainId => !transports?.[chainId])
    if (missingTransportChainIds.length > 0) {
      throw new Error(
        `getWagmiAdapter: explicitOnly mode requires explicit \`transports\` for every chain (missing: ${missingTransportChainIds.join(', ')})`
      )
    }
  }

  // Explicit-only callers own the connector list and transports. Otherwise, E2E callers may
  // provide a caller-owned connector (such as the sandbox wallet) or fall back to the harness
  // mock, while normal callers use browser-wallet discovery.
  const _connectors = options.explicitOnly
    ? (connectors ?? [])
    : options.isE2E
      ? (connectors ?? e2eConnectors())
      : (connectors ?? defaultConnectors)

  // An explicit `storage` option wins (e.g. localStorage for Safe-app iframes).
  // Otherwise default to cookie storage, only disabled if explicitly requested.
  const storage =
    options.storage ??
    (options.useCookieStorage ? createStorage({ storage: cookieStorage }) : undefined)

  // AppKit extends caller transports with a Reown fallback inside WagmiAdapter. Explicit-only
  // callers own one plain config, which is also installed back onto the public adapter seam before
  // `createAppKit` can construct watchers from it. Existing E2E callers still need their separate
  // mock config, but AppKit must not receive and mutate it with browser-wallet connectors.
  // Build either plain config first and do not also install an explicit-only connector factory on
  // the adapter's discarded config: injected connectors keep event-listener closures, so setting
  // one factory up twice routes account-change events to the first (discarded) config.
  // E2E callers that supply their own connectors get the same treatment, since the standalone config
  // is the one the app uses.
  const standaloneConfig =
    options.explicitOnly || options.isE2E
      ? createConfig({
          storage,
          ssr: options.ssr,
          chains: _chains as [Chain, ...Chain[]],
          transports: _transports,
          connectors: _connectors,
          batch,
          ...(options.explicitOnly ? { multiInjectedProviderDiscovery: false } : {})
        })
      : undefined

  const wagmiAdapter = new MorphoWagmiAdapter({
    storage,
    ssr: options.ssr,
    projectId,
    networks: _chains,
    transports: _transports,
    connectors: options.explicitOnly || (options.isE2E && connectors) ? [] : _connectors,
    batch
  })

  const wagmiConfig = standaloneConfig ?? wagmiAdapter.wagmiConfig

  if (options.explicitOnly) wagmiAdapter.wagmiConfig = wagmiConfig

  return { wagmiConfig, wagmiAdapter }
}
